/**
 * IAB Classifier Workflow State
 *
 * TypeScript port of Python WorkflowState from:
 * src/email_parser/workflow/state.py (lines 15-404)
 *
 * This file provides:
 * - WorkflowState interface (LangGraph.js state schema)
 * - State initialization and manipulation helpers
 * - Batch processing utilities
 *
 * MIGRATION NOTE: This is an EXACT 1:1 translation of the Python implementation.
 * Every type, field, and function has been verified against the Python source.
 */
import { Annotation } from '@langchain/langgraph/web';
import { getBatchFromState, hasMoreBatches, advanceToNextBatch as batchOptimizerAdvance, calculateBatchSize, } from './batchOptimizer';
// ============================================================================
// STATE INITIALIZATION
// ============================================================================
/**
 * Create initial workflow state
 *
 * Python source: state.py:201-256
 *
 * @param user_id - User ID for whom to build profile
 * @param emails - List of email objects to process
 * @param force_reprocess - Whether to reprocess already-processed emails
 * @param model_context_window - LLM context window size (optional)
 * @returns Initial WorkflowState
 */
export function createInitialState(user_id, emails, force_reprocess = false, model_context_window) {
    // Python line 224: Import to avoid circular dependency
    // from ..workflow.batch_optimizer import calculate_batch_size
    // Python lines 227-233: Calculate batch size
    let initial_batch_size = 1;
    if (model_context_window) {
        // Python lines 228-233:
        // initial_batch_size = calculate_batch_size(
        //     emails=emails,
        //     context_window=model_context_window,
        //     start_index=0
        // )
        initial_batch_size = calculateBatchSize(emails, model_context_window, 0);
    }
    // Python lines 235-256: Return WorkflowState
    return {
        user_id,
        emails,
        processed_email_ids: [],
        current_email_index: 0,
        total_emails: emails.length,
        force_reprocess,
        current_batch_start: 0,
        batch_size: initial_batch_size,
        model_context_window,
        demographics_results: [],
        household_results: [],
        interests_results: [],
        purchase_results: [],
        reconciliation_data: [],
        errors: [],
        warnings: [],
        next_analyzers: [],
        completed_analyzers: [],
    };
}
// ============================================================================
// STATE QUERY HELPERS
// ============================================================================
/**
 * Get current email from state (DEPRECATED - use get_current_batch instead)
 *
 * Python source: state.py:259-283
 *
 * @param state - Current workflow state
 * @returns Current email or undefined if no more emails
 */
export function getCurrentEmail(state) {
    // Python lines 276-277: Check existence
    if (!state.emails || state.emails.length === 0) {
        return undefined;
    }
    // Python lines 279-281: Bounds check
    const index = state.current_email_index ?? 0;
    if (index >= state.emails.length) {
        return undefined;
    }
    // Python line 283: Return
    return state.emails[index];
}
/**
 * Get current batch of emails from state
 *
 * Python source: state.py:286-301
 *
 * Delegates to batch_optimizer.get_batch_from_state()
 *
 * @param state - Current workflow state
 * @returns Array of emails in current batch
 */
export function getCurrentBatch(state) {
    // Python lines 300-301: Delegate to batch_optimizer
    // from ..workflow.batch_optimizer import get_batch_from_state
    // return get_batch_from_state(state)
    return getBatchFromState(state);
}
/**
 * Check if there are more emails to process
 *
 * Python source: state.py:304-333
 *
 * @param state - Current workflow state
 * @returns True if more emails remain
 */
export function hasMoreEmails(state) {
    // Python lines 322-323: Check existence
    if (!state.emails || state.emails.length === 0) {
        return false;
    }
    // Python lines 326-333: Batch vs single-email logic
    if (state.current_batch_start !== undefined) {
        // Batch processing mode
        // from ..workflow.batch_optimizer import has_more_batches
        // return has_more_batches(state)
        return hasMoreBatches(state);
    }
    else {
        // Single-email mode (DEPRECATED)
        const current_index = state.current_email_index ?? 0;
        const total = state.total_emails ?? 0;
        return current_index < total;
    }
}
// ============================================================================
// STATE MUTATION HELPERS
// ============================================================================
/**
 * Advance to next email/batch
 *
 * Python source: state.py:336-368
 *
 * IMPORTANT: This function MUTATES the state object (Python pattern)
 * In TypeScript/LangGraph.js, we may want to return a new state object instead
 *
 * @param state - Current workflow state (will be mutated)
 * @returns Updated state
 */
export function advanceToNextEmail(state) {
    // Python lines 353-358: Advance logic
    if (state.current_batch_start !== undefined) {
        // Batch processing mode
        // from ..workflow.batch_optimizer import advance_to_next_batch
        // state = advance_to_next_batch(state)
        batchOptimizerAdvance(state); // Mutates state in-place
    }
    else {
        // Single-email mode (DEPRECATED)
        state.current_email_index = (state.current_email_index ?? 0) + 1;
    }
    // Python lines 361-366: Reset analyzer results
    state.demographics_results = [];
    state.household_results = [];
    state.interests_results = [];
    state.purchase_results = [];
    state.next_analyzers = [];
    state.completed_analyzers = [];
    // Python line 368: Return
    return state;
}
/**
 * Add error message to state
 *
 * Python source: state.py:371-386
 *
 * @param state - Current workflow state (will be mutated)
 * @param error_message - Error message to add
 * @returns Updated state
 */
export function addError(state, error_message) {
    // Python lines 382-383: Initialize if needed
    if (!state.errors) {
        state.errors = [];
    }
    // Python line 385: Append
    state.errors.push(error_message);
    return state;
}
/**
 * Add warning message to state
 *
 * Python source: state.py:389-404
 *
 * @param state - Current workflow state (will be mutated)
 * @param warning_message - Warning message to add
 * @returns Updated state
 */
export function addWarning(state, warning_message) {
    // Python lines 400-401: Initialize if needed
    if (!state.warnings) {
        state.warnings = [];
    }
    // Python line 403: Append
    state.warnings.push(warning_message);
    return state;
}
/**
 * WorkflowState Annotation Schema for LangGraph.js
 *
 * LangGraph.js requires Annotation.Root() to create a state schema that can
 * be passed to StateGraph constructor.
 *
 * This creates the actual state schema object from the WorkflowState interface.
 */
export const WorkflowState = Annotation.Root({
    // Required fields
    user_id: (Annotation),
    // Email data
    emails: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    processed_email_ids: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    current_email_index: Annotation({
        value: (_prev, current) => current,
        default: () => 0,
    }),
    total_emails: Annotation({
        value: (_prev, current) => current,
        default: () => 0,
    }),
    // Existing profile
    existing_profile: Annotation({
        value: (_prev, current) => current,
        default: () => ({}),
    }),
    // Analyzer results
    demographics_results: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    household_results: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    interests_results: Annotation({
        value: (prev, current) => prev.concat(current),
        default: () => [],
    }),
    purchase_results: Annotation({
        value: (prev, current) => prev.concat(current),
        default: () => [],
    }),
    // Reconciliation
    reconciliation_data: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    updated_profile: Annotation({
        value: (_prev, current) => current,
        default: () => ({}),
    }),
    // Error tracking
    errors: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    warnings: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    // Timestamps
    workflow_started_at: Annotation({
        value: (_prev, current) => current,
        default: () => '',
    }),
    workflow_completed_at: Annotation({
        value: (_prev, current) => current,
        default: () => '',
    }),
    // Batch processing
    batch_size: Annotation({
        value: (_prev, current) => current,
        default: () => 1,
    }),
    current_batch_index: Annotation({
        value: (_prev, current) => current,
        default: () => 0,
    }),
    current_batch_start: Annotation({
        value: (_prev, current) => current,
        default: () => 0,
    }),
    total_batches: Annotation({
        value: (_prev, current) => current,
        default: () => 0,
    }),
    batch_mode: Annotation({
        value: (_prev, current) => current,
        default: () => false,
    }),
    model_context_window: Annotation({
        value: (_prev, current) => current,
        default: () => 0,
    }),
    force_reprocess: Annotation({
        value: (_prev, current) => current,
        default: () => false,
    }),
    // LLM config
    cost_tracker: Annotation({
        value: (_prev, current) => current,
        default: () => null,
    }),
    tracker: Annotation({
        value: (_prev, current) => current,
        default: () => null,
    }),
    llm_provider: Annotation({
        value: (_prev, current) => current,
        default: () => '',
    }),
    llm_model: Annotation({
        value: (_prev, current) => current,
        default: () => '',
    }),
    llm_config: Annotation({
        value: (_prev, current) => current,
        default: () => null,
    }),
    llm_client: Annotation({
        value: (_prev, current) => current,
        default: () => null,
    }),
    llm_route_client_factory: Annotation({
        value: (_prev, current) => current,
        default: () => null,
    }),
    llm_routes: Annotation({
        value: (_prev, current) => current,
        default: () => ({}),
    }),
    // Routing (deprecated)
    next_analyzers: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
    completed_analyzers: Annotation({
        value: (_prev, current) => current,
        default: () => [],
    }),
});
//# sourceMappingURL=state.js.map